import {
  differenceInMinutes,
  differenceInHours,
  subHours,
  differenceInYears,
  subYears,
  differenceInDays,
  differenceInMonths,
  subDays,
  subMonths,
  startOfWeek,
  endOfWeek,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
} from "date-fns";
import { Event } from "../../../api/src/types";
import { PurchaseItemWithEventsGetResponse } from "../../../api/src/users/purchase-items/route";

export interface TimeDifference {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
}

export const getCountDownTimes = (timestamp: string): TimeDifference => {
  const targetDate = new Date(timestamp);
  const currentDate = new Date();

  const years = Math.max(0, differenceInYears(targetDate, currentDate));
  const targetMinusYears = subYears(targetDate, years);
  const months = Math.max(0, differenceInMonths(targetMinusYears, currentDate));
  const targetMinusMonths = subMonths(targetMinusYears, months);
  const days = Math.max(0, differenceInDays(targetMinusMonths, currentDate));
  const targetMinusDays = subDays(targetMinusMonths, days);
  const hours = Math.max(0, differenceInHours(targetMinusDays, currentDate));
  const targetMinusHours = subHours(targetMinusDays, hours);
  const minutes = Math.max(
    0,
    differenceInMinutes(targetMinusHours, currentDate),
  );

  return {
    years,
    months,
    days,
    hours,
    minutes,
  };
};

export const formatDateForHTMLInput = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";

  try {
    let dateObj: Date;

    if (typeof date === "string") {
      dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        return `${dateObj.getUTCFullYear()}-${String(dateObj.getUTCMonth() + 1).padStart(2, "0")}-${String(dateObj.getUTCDate()).padStart(2, "0")}`;
      }
      return "";
    } else {
      dateObj = date;
      if (isNaN(dateObj.getTime())) {
        return "";
      }
      return `${dateObj.getUTCFullYear()}-${String(dateObj.getUTCMonth() + 1).padStart(2, "0")}-${String(dateObj.getUTCDate()).padStart(2, "0")}`;
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

export const validateDateOfBirth = (date: string): string | null => {
  const enteredDate = parseISO(date);
  const today = startOfDay(new Date());
  const minDate = subYears(today, 90);
  const maxDate = subYears(today, 18);

  if (isAfter(enteredDate, today)) {
    return "Date cannot be in the future.";
  } else if (isBefore(enteredDate, minDate)) {
    return "Date cannot be more than 90 years ago.";
  } else if (isAfter(enteredDate, maxDate)) {
    return "You must be at least 18 years old";
  }
  return null;
};

export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return "";
  return new Date(date).toLocaleString();
};

export const formatShortDate = (date: Date | string): string => {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTime = (date: Date | string): string => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatAppointmentDate = (
  event: Event | PurchaseItemWithEventsGetResponse[number]["events"][number],
  type: "short" | "long" | "time" | "year",
): string => {
  const startDate = new Date(event.eventStartAt);
  const endDate = new Date(event.eventEndAt);
  const month = startDate.toLocaleDateString("en-US", {
    month: type === "short" || type === "year" ? "short" : "long",
  });
  const day = startDate.toLocaleDateString("en-US", { day: "numeric" });
  const year = startDate.toLocaleDateString("en-US", { year: "numeric" });
  const startHour = startDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  const endHour = endDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  switch (type) {
    case "short":
      return `${startHour} - ${endHour}, ${month} ${day}`;
    case "long":
      return `${startHour} - ${endHour}, ${month} ${day}`;
    case "time":
      return `${startHour} - ${endHour}`;
    case "year":
      return `${startHour} - ${endHour}, ${month} ${day}, ${year}`;
  }
};

export const getStartAndEndOfWeek = (
  date: Date,
): { start: Date; end: Date } => {
  const start = startOfWeek(date);
  const end = endOfWeek(date);
  return { start, end };
};

export const isDateInFuture = (date: string | null): boolean => {
  if (!date) return false;

  // Parse date components to avoid timezone issues
  const dateParts = date.split("-");
  if (dateParts.length !== 3) {
    console.error("Invalid date format:", date);
    return false;
  }

  const year = parseInt(dateParts[0] ?? "", 10);
  const month = parseInt(dateParts[1] ?? "", 10) - 1; // Month is 0-indexed
  const day = parseInt(dateParts[2] ?? "", 10);

  const targetDate = new Date(year, month, day);
  const today = new Date();

  // Check if the date is valid
  if (isNaN(targetDate.getTime())) {
    return false;
  }

  // Reset time to start of day for accurate date comparison
  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return targetDate > today;
};

export const formatGenericCardAppointmentDate = (
  event: Event | PurchaseItemWithEventsGetResponse[number]["events"][number],
): { hour: string; date: string } => {
  const startDate = new Date(event.eventStartAt);
  const endDate = new Date(event.eventEndAt);
  const month = startDate.toLocaleDateString("en-US", {
    month: "short",
  });
  const day = startDate.toLocaleDateString("en-US", { day: "numeric" });
  const year = startDate.toLocaleDateString("en-US", { year: "numeric" });
  const weekday = startDate.toLocaleDateString("en-US", { weekday: "long" });
  const startHour = startDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  const endHour = endDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return {
    hour: `${startHour} - ${endHour}`,
    date: `${weekday}, ${month} ${day}, ${year}`,
  };
};
